import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex gap-x-2.5" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "flex items-center gap-x-4" }
const _hoisted_4 = {
  key: 0,
  class: "flex gap-x-4 w-full items-start"
}
const _hoisted_5 = { class: "flex-1" }
const _hoisted_6 = { class: "flex-1" }
const _hoisted_7 = {
  key: 1,
  class: "flex gap-4"
}
const _hoisted_8 = { class: "flex flex-col gap-y-2" }
const _hoisted_9 = { class: "flex gap-x-4 pt-3" }

import { computed, ref, reactive, watch, onMounted, nextTick, onUpdated } from "vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsTextarea from "@/components/GgmsTextarea.vue"
import GgmsCheckBox from "@/components/GgmsCheckBox.vue"
import GgmsDropdown from "@/components/GgmsDropdown.vue"
import GgmsMultiSelect from "@/components/GgmsMultiSelect.vue"
import GgmsLoading from "@/components/GgmsLoading.vue"
import GgmsCalendar from "@/components/GgmsCalendar.vue"
import { CalendarIcon, ClockIcon, CheckCircleIcon as CheckCircleIconOutline } from "@heroicons/vue/outline"
import { CheckCircleIcon } from "@heroicons/vue/solid"
import { useTaskStore } from "@/stores/task"
import { useAgentsStore } from "@/stores/agents"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useAuthStore } from "@/stores/auth"
import { useAgencyStore } from "@/stores/agency"
import { useContactsStore } from "@/stores/contacts"
import { useWorkflowStore } from "@/stores/workflow"
import { useTagStore } from "@/stores/tag"
import {
    isInvalid,
    hasErrorMessage,
    isAdmin,
    getDomainId,
    getTaskTypes,
    getTaskReminderOptions,
} from "@/shared/utils/helpers"
import { formatters, predicates, getters } from "@/shared/utils/dateUtils"
import { Task } from "@/shared/models/task"
import { Tag } from "@/shared/models/tag"
import { Agent } from "@/shared/models/agent"
import { Url } from "@/shared/models/agency"
import { Contact } from "@/shared/models/contact"

interface Props {
    modalAction?: string
    taskId?: string
    contactId?: string
    isNotEditableByUser?: boolean
    isCreateTaskModalOpen?: boolean
    isForWorkflow?: boolean
    isUpdateTask?: boolean
    taskValue?: Task
    errorValue?: { field: string; message: string }
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateTaskForm',
  props: {
    modalAction: {},
    taskId: {},
    contactId: {},
    isNotEditableByUser: { type: Boolean },
    isCreateTaskModalOpen: { type: Boolean },
    isForWorkflow: { type: Boolean },
    isUpdateTask: { type: Boolean },
    taskValue: {},
    errorValue: {}
  },
  emits: ["update:task"],
  setup(__props: any, { emit: __emit }) {

const taskStore = useTaskStore()
const agentStore = useAgentsStore()
const authStore = useAuthStore()
const agencyStore = useAgencyStore()
const gridCollectionStore = useGridCollectionStore()
const workflowStore = useWorkflowStore()
const tagStore = useTagStore()
const contactStore = useContactsStore()

const props = __props
const emit = __emit

const form = ref()
const error = ref()
const isTaskLoading = ref(true)
const queueDate = ref("")
const queueTime = ref("")
const firstInput = ref()
const isFirstLoad = ref(true)

const tags = ref<Tag[]>([])
const tag = ref<string[]>([])

const taskTypes = ref(getTaskTypes())
const reminderOptions = ref(getTaskReminderOptions())

const cachedAgents = computed(() =>
    agentStore.tableState.data.filter((agent: Agent) => agent.status !== "pending" && agent.fullName)
)
const cachedContacts = computed(() =>
    contactStore.tableState.data.map((contact) => ({
        ...contact,
        fullName: getContactName(contact),
    }))
)

const cachedTags = computed(() => tagStore.cachedTags)
const currentUser = computed(() => authStore.currentUser)
const collections = computed(() => gridCollectionStore.collectionTableState.data)

const isSelectedDateToday = ref(false)
const minHour = computed(() => (isSelectedDateToday.value ? "now" : "today"))

const task: Task = reactive({
    _id: "",
    displayName: "",
    description: "",
    contact: {
        _id: "",
        emails: [],
        tasks: { data: [], total: 0 },
        timelineItems: { data: [], total: 0 },
        uploads: { data: [], total: 0 },
    } as Contact,
    when: {
        startAt: "",
        missedAt: "",
        timezone: "America/Los_Angeles",
    },
    reminder: reminderOptions.value[0].attribute,
    shouldMarkAsMissedIfNotCompleted: true,
    isAssignedToTriggeringContact: true,
    agent: {
        _id: currentUser?.value?._id,
        fullName: currentUser?.value?.fullName,
    },
    agentId: currentUser?.value?._id,
    collection: {
        _id: "",
        displayName: "",
    },
    type: "",
    collectionId: "",
    status: "",
    notes: "",
    domainId: "",
    tags: [],
    relativeDueDateDays: "",
    unitsOfTimeToMarkAsMissed: "30",
    typeOfTimeToMarkAsMissed: "days",
})

const domainOptions = computed(() =>
    agencyStore.agency.agencyDomains.map((domain) => ({ url: (domain.url as Url).domain, _id: domain._id }))
)
const notCompletedOptions = [
    { value: "days", attribute: "days" },
    { value: "hours", attribute: "hours" },
    { value: "minutes", attribute: "minutes" },
]
const selectedDomain = ref(initializeDomain())

const isTaskCompleted = computed(() => task.status === "completed")
const getCheckCircleIconType = computed(() => (isTaskCompleted.value ? CheckCircleIcon : CheckCircleIconOutline))
const getStatusIconColor = computed(() => {
    if (isTaskCompleted.value) {
        return props.isUpdateTask
            ? "fill-primary-color-600 text-white"
            : "fill-primary-color-600 text-white hover:fill-primary-color-700"
    } else {
        return props.isUpdateTask ? "text-gray-400 hover:text-gray-500" : "text-gray-400"
    }
})
const getStatusCursorType = computed(() => {
    if (props.isNotEditableByUser) {
        return "cursor-not-allowed"
    }
    if (props.modalAction === "edit") {
        return "cursor-pointer"
    }
    return ""
})

function setQueueDateAndTime(date: string, isForWorkflow = false) {
    //  if it is for workflow, don't convert anything
    if (isForWorkflow) {
        queueDate.value = date.split("T")[0]
        queueTime.value = date.split("T")[1].slice(0, 5)
        return
    }
    const isoDateInAgencyTimezone = formatters.formatDateToIso(date)
    const [datePart, timePart] = isoDateInAgencyTimezone.split("T")
    queueDate.value = datePart
    queueTime.value = timePart.slice(0, 5)
}

function setTaskStartDate() {
    try {
        const date = getters.getNewDate(queueDate.value)
        isSelectedDateToday.value = predicates.isToday(date)
        const [hours, minutes] = getters.getHoursAndMinutes(queueTime.value)
        if (task.when === undefined) {
            task.when = {
                startAt: formatters.formatDateToIso(
                    new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes)
                ),
                missedAt: formatters.formatDateToIso(
                    new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes)
                ),
                timezone: "America/Los_Angeles",
            }
        } else {
            // Manually set the date and time as we don't want other conversions
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, "0")
            const day = String(date.getDate()).padStart(2, "0")
            const hour = String(hours).padStart(2, "0")
            const minute = String(minutes).padStart(2, "0")
            task.when.startAt = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`
        }
    } catch (error) {
        return
    }
}

function getContactName(contact: Contact) {
    if (!contact) return ""
    const { firstName, lastName } = contact
    if (firstName && lastName) return `${firstName} ${lastName}`
    return firstName || lastName || contact.emails[0].emailAddress || "Unknown"
}

async function changeStatusOfTask() {
    if (props.isNotEditableByUser) {
        return
    }

    if (props.taskId) {
        if (props.modalAction === "edit") {
            const status = isTaskCompleted.value ? "pending" : "completed"
            await taskStore.updateTaskStatus(props.taskId, status)
            task.status = status
            taskStore.getTasks()
        }
    }
}

function initializeDomain() {
    const domain = getDomainId()
    task.domainId = domain
    return domain === "all" ? null : domain
}

function scrollToErrorField() {
    if (props.errorValue && !props.errorValue.field) {
        return
    }
    const errorField = form.value.querySelector(`[name="${error.value.field}"]`) || form.value.querySelector("input")
    if (errorField) {
        errorField.scrollIntoView({ behavior: "smooth" })
    }
}

function checkDate() {
    if (queueDate.value && queueTime.value && predicates.isEarlierToday(queueDate.value, queueTime.value)) {
        const newDate = new Date()
        queueTime.value = `${newDate.getHours()}:${newDate.getMinutes() < 10 ? "0" : ""}${newDate.getMinutes()}`
    }
}

onUpdated(() => {
    nextTick(() => {
        const inputElement = firstInput.value?.$el.querySelector("input")
        if (inputElement && isFirstLoad.value) {
            inputElement.focus()
            isFirstLoad.value = false
        }
    })
})

onMounted(async () => {
    gridCollectionStore.collectionType = "task"
    if (isAdmin() && !cachedAgents.value.length) {
        await agentStore.loadAgents()
    }
    if (contactStore.tableState.data.length === 0) {
        await contactStore.loadContacts()
    }
    //get tags for task
    tags.value = await tagStore.getTagsForTasks()

    if (props.isForWorkflow) {
        if (!workflowStore.existingTaskStartAt) {
            setQueueDateAndTime(new Date().toISOString())
        } else {
            setQueueDateAndTime(workflowStore.existingTaskStartAt, props.isForWorkflow)
        }
        if (workflowStore.modelValue) {
            Object.assign(task, workflowStore.modelValue)
            if (workflowStore.modelValue.domainId) {
                task.domainId = workflowStore.modelValue.domainId
            } else {
                initializeDomain()
            }
            if (workflowStore.modelValue.tags) tag.value = workflowStore.modelValue.tags.map((tag: Tag) => tag._id)
            else tag.value = []
            task.unitsOfTimeToMarkAsMissed = workflowStore.modelValue.unitsOfTimeToMarkAsMissed || "30"
            task.typeOfTimeToMarkAsMissed = workflowStore.modelValue.typeOfTimeToMarkAsMissed || "days"
        }
        isTaskLoading.value = false
    } else if (props.taskId) {
        try {
            isTaskLoading.value = true
            const response = await taskStore.getTask(props.taskId)
            task.displayName = response.task.displayName
            task.description = response.task.description
            task.when = {
                startAt: response.task.when?.startAt,
                missedAt: response.task.when?.missedAt,
                timezone: response.task.when?.timezone || "America/Los_Angeles",
            }
            task.reminder =
                reminderOptions.value.find((option) => option.attribute === response.task.reminder)?.attribute ||
                "minutes_30"
            task.shouldMarkAsMissedIfNotCompleted = response.task.shouldMarkAsMissedIfNotCompleted || false
            task.isAssignedToTriggeringContact = response.task.isAssignedToTriggeringContact || false
            task.agent = {
                _id: response.task.agent?._id,
                fullName: response.task.agent?.fullName,
            }
            if (response.task.agent._id) task.agentId = response.task.agent?._id
            task.type = taskTypes.value.find((option) => option.attribute === response.task.type)?.attribute || ""
            task.collection = {
                _id: response.task.collection?._id,
                displayName: response.task.collection?.displayName,
            }
            task.collectionId = response.task.collection?._id
            task.contact = response.task.contact
            task.contactId = response.task.contact?._id
            task.status = response.task.status
            task.notes = response.task.notes
            task.domainId = response.task.domainId || ""
            selectedDomain.value = domainOptions.value.find((option) => option._id === task?.domainId)?._id
            if (task.when) {
                setQueueDateAndTime(task.when.startAt)
            }
            if (response.task.tags) tag.value = response.task.tags.map((tag) => tag._id)
            else tag.value = []
            task.unitsOfTimeToMarkAsMissed = response.task.unitsOfTimeToMarkAsMissed || "30"
            task.typeOfTimeToMarkAsMissed = response.task.typeOfTimeToMarkAsMissed || "days"
            isTaskLoading.value = false
        } catch (e) {
            console.log(`There was an error getting task with id: ${props.taskId}`, e)
        } finally {
            isTaskLoading.value = false
        }
    } else {
        setQueueDateAndTime(new Date().toISOString())
        isTaskLoading.value = false
    }
})

watch(
    () => agencyStore.selectedDomain,
    (newValue) => {
        selectedDomain.value = newValue._id === "all" ? "" : newValue._id
    }
)

watch(
    () => queueDate.value,
    () => {
        setTaskStartDate()
    }
)

watch(
    () => queueTime.value,
    () => {
        setTaskStartDate()
    }
)

watch(
    () => selectedDomain.value,
    () => {
        task.domainId = selectedDomain.value
    }
)

watch(
    () => task,
    () => {
        setTaskStartDate()
        emit("update:task", task)
    },
    { deep: true }
)

watch(
    () => tag,
    () => {
        task.tags = tag.value.map((tagId) => cachedTags.value[tagId])
    },
    { deep: true }
)

watch(
    () => props.errorValue,
    () => {
        if (!props.errorValue) {
            error.value = {
                field: "",
                message: "",
            }
            return
        }
        error.value = props.errorValue
        scrollToErrorField()
    }
)

watch(
    () => task,
    () => {
        if (props.isForWorkflow) {
            workflowStore.areButtonDisabled = !task.displayName || !task.type || !task.when
        }
    },
    { deep: true }
)

watch(
    () => workflowStore.modelValue,
    () => {
        if (props.isForWorkflow) {
            task.displayName = workflowStore.modelValue.displayName
            task.type = workflowStore.modelValue.type
            task.collectionId = workflowStore.modelValue.collectionId
            task.agentId = workflowStore.modelValue.agentId
            task.status = workflowStore.modelValue.status
            task.description = workflowStore.modelValue.description
            task.notes = workflowStore.modelValue.notes
            task.domainId = workflowStore.modelValue.domainId
            task.tags = workflowStore.modelValue.tags
            task.unitsOfTimeToMarkAsMissed = workflowStore.modelValue.unitsOfTimeToMarkAsMissed
            task.typeOfTimeToMarkAsMissed = workflowStore.modelValue.typeOfTimeToMarkAsMissed
            task.shouldMarkAsMissedIfNotCompleted = workflowStore.modelValue.shouldMarkAsMissedIfNotCompleted
            task.isAssignedToTriggeringContact = workflowStore.modelValue.isAssignedToTriggeringContact
            task.reminder = workflowStore.modelValue.reminder
            task.when = workflowStore.modelValue.when
            task.agent = workflowStore.modelValue.agent
            task.contact = workflowStore.modelValue.contact
            task.contactId = workflowStore.modelValue.contactId
            task.agentId = workflowStore.modelValue.agentId
            task.collection = workflowStore.modelValue.collection
        }
    },
    { deep: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createVNode(GgmsLoading, null, null, 512), [
      [_vShow, isTaskLoading.value]
    ]),
    _withDirectives(_createElementVNode("div", {
      ref_key: "form",
      ref: form,
      class: "flex flex-col gap-y-[30px]"
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(GgmsInput, {
          modelValue: task.displayName,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((task.displayName) = $event)),
          ref_key: "firstInput",
          ref: firstInput,
          class: "grow",
          name: "task-name",
          label: "Task Name",
          required: true,
          overwriteShowErrors: true,
          invalid: _unref(isInvalid)('displayname', error.value),
          errorMessage: _unref(hasErrorMessage)('displayname', error.value)
        }, null, 8, ["modelValue", "invalid", "errorMessage"]),
        (!_ctx.isForWorkflow && _ctx.isUpdateTask)
          ? (_openBlock(), _createBlock(_resolveDynamicComponent(getCheckCircleIconType.value), {
              key: 0,
              class: _normalizeClass(["h-9 w-9 mt-7", [getStatusIconColor.value, getStatusCursorType.value]]),
              onClick: changeStatusOfTask
            }, null, 8, ["class"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(["flex gap-4", _ctx.isForWorkflow ? 'flex-col' : 'flex-row max-md:flex-wrap'])
      }, [
        _createVNode(GgmsDropdown, {
          modelValue: task.type,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((task.type) = $event)),
          options: taskTypes.value,
          name: "task-type",
          label: "Task Type",
          optionLabel: "value",
          optionValue: "attribute",
          placeholder: "Select task type",
          required: true,
          invalid: _unref(isInvalid)('type', error.value),
          errorMessage: _unref(hasErrorMessage)('type', error.value),
          class: "w-full"
        }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
        _createVNode(GgmsDropdown, {
          modelValue: task.collectionId,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((task.collectionId) = $event)),
          options: collections.value,
          name: "task-queue",
          label: "Task Collection",
          optionLabel: "displayName",
          optionValue: "_id",
          placeholder: "Select task collection",
          class: "w-full",
          overwriteShowErrors: true
        }, null, 8, ["modelValue", "options"])
      ], 2),
      _createVNode(GgmsDropdown, {
        modelValue: task.agentId,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((task.agentId) = $event)),
        options: cachedAgents.value,
        overwriteShowErrors: true,
        name: "agents",
        label: "Assigned to",
        optionLabel: "fullName",
        optionValue: "_id",
        placeholder: "Select agent",
        invalid: _unref(isInvalid)('agentid', error.value),
        errorMessage: _unref(hasErrorMessage)('agentid', error.value),
        class: "w-full"
      }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
      _createVNode(GgmsDropdown, {
        modelValue: task.contactId,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((task.contactId) = $event)),
        options: cachedContacts.value,
        overwriteShowErrors: true,
        name: "contacts",
        label: "Associated Contact",
        optionLabel: "fullName",
        isEditable: true,
        optionValue: "_id",
        placeholder: "Select contact",
        invalid: _unref(isInvalid)('contactid', error.value),
        errorMessage: _unref(hasErrorMessage)('contactid', error.value),
        disabled: _ctx.isForWorkflow && task.isAssignedToTriggeringContact,
        class: "w-full"
      }, null, 8, ["modelValue", "options", "invalid", "errorMessage", "disabled"]),
      (_ctx.isForWorkflow)
        ? (_openBlock(), _createBlock(GgmsCheckBox, {
            key: 0,
            modelValue: task.isAssignedToTriggeringContact,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((task.isAssignedToTriggeringContact) = $event)),
            name: "is-assigned-to-triggering-contact",
            label: "Assign this task to the contact enrolled in the workflow",
            isLabelClickable: true
          }, null, 8, ["modelValue"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", null, [
        _createElementVNode("div", {
          class: _normalizeClass(["flex max-sm:flex-wrap gap-4", _ctx.isForWorkflow ? 'flex-col gap-y-10' : 'flex-row'])
        }, [
          _createElementVNode("div", null, [
            (!_ctx.isForWorkflow)
              ? (_openBlock(), _createElementBlock("p", _hoisted_2, [
                  _cache[18] || (_cache[18] = _createTextVNode(" Due date ")),
                  _createElementVNode("span", {
                    class: _normalizeClass([_unref(isInvalid)('when.startat', error.value) ? 'text-red-500' : ''])
                  }, "*", 2)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_3, [
              (_ctx.isForWorkflow)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, [
                      _createVNode(GgmsInput, {
                        modelValue: task.relativeDueDateDays,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((task.relativeDueDateDays) = $event)),
                        overwriteShowErrors: true,
                        label: "Days Later",
                        class: "w-full",
                        placeholder: "Enter days"
                      }, null, 8, ["modelValue"])
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                      (Number(task.relativeDueDateDays) !== 0)
                        ? (_openBlock(), _createBlock(GgmsCalendar, {
                            key: 0,
                            id: "queueTime",
                            modelValue: queueTime.value,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((queueTime).value = $event)),
                            label: "Time",
                            position: "below",
                            showTime: true,
                            showDate: false,
                            invalid: _unref(isInvalid)('when.startat', error.value),
                            errorMessage: _unref(hasErrorMessage)('when.startat', error.value),
                            iconStart: _unref(ClockIcon),
                            minDate: Number(task.relativeDueDateDays) === 0 ? minHour.value : 'today',
                            class: "w-full"
                          }, null, 8, ["modelValue", "invalid", "errorMessage", "iconStart", "minDate"]))
                        : (_openBlock(), _createBlock(GgmsInput, {
                            key: 1,
                            label: "Time",
                            modelValue: 'Immediately',
                            disabled: true,
                            iconStart: _unref(ClockIcon),
                            class: "w-full"
                          }, null, 8, ["iconStart"]))
                    ])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _createVNode(GgmsCalendar, {
                      modelValue: queueDate.value,
                      "onUpdate:modelValue": [
                        _cache[8] || (_cache[8] = ($event: any) => ((queueDate).value = $event)),
                        checkDate
                      ],
                      position: "below",
                      showTime: false,
                      showDate: true,
                      invalid: _unref(isInvalid)('when.startat', error.value),
                      errorMessage: _unref(hasErrorMessage)('when.startat', error.value),
                      iconStart: _unref(CalendarIcon),
                      minDate: "today",
                      class: _normalizeClass([_ctx.isForWorkflow ? 'flex-1' : 'w-[150px]'])
                    }, null, 8, ["modelValue", "invalid", "errorMessage", "iconStart", "class"]),
                    _createVNode(GgmsCalendar, {
                      modelValue: queueTime.value,
                      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((queueTime).value = $event)),
                      position: "below",
                      showTime: true,
                      showDate: false,
                      invalid: _unref(isInvalid)('when.startat', error.value),
                      errorMessage: _unref(hasErrorMessage)('when.startat', error.value),
                      iconStart: _unref(ClockIcon),
                      minDate: minHour.value,
                      class: _normalizeClass([_ctx.isForWorkflow ? 'flex-1' : 'w-36'])
                    }, null, 8, ["modelValue", "invalid", "errorMessage", "iconStart", "minDate", "class"])
                  ]))
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _cache[19] || (_cache[19] = _createElementVNode("span", { class: "text-sm font-medium text-gray-700" }, "Set Reminder", -1)),
            _createVNode(GgmsDropdown, {
              modelValue: task.reminder,
              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((task.reminder) = $event)),
              options: reminderOptions.value,
              overwriteShowErrors: true,
              name: "reminder",
              optionLabel: "value",
              optionValue: "attribute",
              placeholder: "Select reminder",
              class: "w-full shadow-sm rounded-md"
            }, null, 8, ["modelValue", "options"])
          ])
        ], 2)
      ]),
      _createElementVNode("div", null, [
        _createVNode(GgmsCheckBox, {
          modelValue: task.shouldMarkAsMissedIfNotCompleted,
          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((task.shouldMarkAsMissedIfNotCompleted) = $event)),
          name: "missed-if-not-completed",
          label: "Mark as Missed if not completed within selected days after Due Date",
          isLabelClickable: true
        }, null, 8, ["modelValue"]),
        _createElementVNode("div", _hoisted_9, [
          _createVNode(GgmsInput, {
            modelValue: task.unitsOfTimeToMarkAsMissed,
            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((task.unitsOfTimeToMarkAsMissed) = $event)),
            disabled: !task.shouldMarkAsMissedIfNotCompleted,
            overwriteShowErrors: true
          }, null, 8, ["modelValue", "disabled"]),
          _createVNode(GgmsDropdown, {
            modelValue: task.typeOfTimeToMarkAsMissed,
            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((task.typeOfTimeToMarkAsMissed) = $event)),
            options: notCompletedOptions,
            overwriteShowErrors: true,
            disabled: !task.shouldMarkAsMissedIfNotCompleted,
            optionLabel: "value",
            optionValue: "attribute",
            placeholder: "Select time",
            class: "w-full"
          }, null, 8, ["modelValue", "disabled"])
        ])
      ]),
      _createVNode(GgmsMultiSelect, {
        modelValue: tag.value,
        "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((tag).value = $event)),
        label: "Add Tags",
        placeholder: "+ Add Tag",
        display: "chip",
        options: tags.value,
        overwriteShowErrors: true,
        optionLabel: "displayName",
        optionValue: "_id",
        invalid: _unref(isInvalid)('tags', error.value),
        errorMessage: _unref(hasErrorMessage)('tags', error.value)
      }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
      _createVNode(GgmsTextarea, {
        modelValue: task.description,
        "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((task.description) = $event)),
        placeholder: "Add description",
        label: "Task Description",
        required: false,
        rows: 5,
        invalid: _unref(isInvalid)('description', error.value),
        errorMessage: _unref(hasErrorMessage)('description', error.value)
      }, null, 8, ["modelValue", "invalid", "errorMessage"]),
      (_ctx.isUpdateTask)
        ? (_openBlock(), _createBlock(GgmsTextarea, {
            key: 1,
            modelValue: task.notes,
            "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((task.notes) = $event)),
            placeholder: "Add notes",
            label: "Notes",
            rows: 5,
            overwriteShowErrors: true
          }, null, 8, ["modelValue"]))
        : _createCommentVNode("", true),
      _createVNode(GgmsDropdown, {
        modelValue: selectedDomain.value,
        "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((selectedDomain).value = $event)),
        options: domainOptions.value,
        label: "Domain",
        name: "domain",
        isShowClear: true,
        optionLabel: "url",
        optionValue: "_id",
        placeholder: "Select Domain",
        class: "w-full",
        invalid: _unref(isInvalid)('domain', error.value),
        "error-message": _unref(hasErrorMessage)('domain', error.value),
        overwriteShowErrors: true
      }, null, 8, ["modelValue", "options", "invalid", "error-message"])
    ], 512), [
      [_vShow, !isTaskLoading.value]
    ])
  ], 64))
}
}

})